// Dark teal-mint theming for the embedded Clerk auth UI (Module K — K4).
//
// DECISION: option A — keep Clerk's auth backend (sessions/MFA/OAuth + the
// worker's JWT verification in middleware/clerk.ts + webhooks/clerk.ts), change
// ONLY the visual surface. This is presentation-only: zero backend/security
// change, no secrets — pure styling derived from the SAME K1 tokens that the
// rest of the ad UI uses (apps/web/src/ad/theme.ts), so Clerk's stock widgets
// stop clashing with the dark app shell.
//
// `clerkAppearance` is passed once on <ClerkProvider appearance={...}> in
// main.tsx; Clerk cascades it to every child component (SignIn / SignUp /
// UserButton / UserProfile / OrganizationProfile). Per-component overrides can
// be layered on top via each component's own `appearance` prop when needed.

import type { ClerkProviderProps } from '@clerk/clerk-react';
import { tokens } from './theme';

// Derive the shape from Clerk's own prop type so we never hand-roll (and drift
// from) Clerk's `Appearance` definition. `appearance` is optional on the props,
// so strip the `undefined` to keep the exported object strongly typed.
type ClerkAppearance = NonNullable<ClerkProviderProps['appearance']>;

export const clerkAppearance: ClerkAppearance = {
  variables: {
    colorPrimary: '#00d4aa', // teal
    colorBackground: '#08080d', // bg0
    colorInputBackground: '#0c0c15', // bg1
    colorText: '#eeeef2', // fg0
    colorTextSecondary: '#9494a8', // fg1
    colorInputText: '#eeeef2',
    colorDanger: '#fca5a5',
    colorSuccess: '#2dffc4',
    borderRadius: '10px', // radius.md
    fontFamily: tokens.font.family,
  },
  elements: {
    // Dark surface card (bg2) lifted off the page with the shared card shadow
    // and a hairline border — matches the rest of the dashboard's surfaces.
    card: {
      backgroundColor: tokens.color.bg2,
      boxShadow: tokens.shadow.card,
      border: `1px solid ${tokens.color.border}`,
    },
    // The brand gradient pill — mirrors buttonStyle('primary') from K1's
    // theme.ts (gradient + bg0 text + pill radius + brand glow).
    formButtonPrimary: {
      backgroundImage: tokens.gradient.brand,
      color: tokens.color.bg0,
      borderRadius: tokens.radius.pill,
      boxShadow: tokens.shadow.pillBrand,
    },
    headerTitle: {
      color: tokens.color.fg0,
    },
    headerSubtitle: {
      color: tokens.color.fg1,
    },
    // OAuth / social buttons: subtle dark surface with a readable mid border.
    socialButtonsBlockButton: {
      backgroundColor: tokens.color.bg1,
      border: `1px solid ${tokens.color.borderMid}`,
      color: tokens.color.fg0,
    },
    // Teal links (e.g. "Sign up" / "Sign in" cross-links in the footer).
    footerActionLink: {
      color: tokens.color.teal,
    },
    // Inputs sit on bg1 with the mid border to match the app's themed fields.
    formFieldInput: {
      backgroundColor: tokens.color.bg1,
      border: `1px solid ${tokens.color.borderMid}`,
      color: tokens.color.fg0,
    },
  },
};
